"use client";

import { FC, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { MdThumbUp, MdThumbDown, MdComment } from "react-icons/md";

import { useStyles } from "./styles";
import { Nullable } from "@/types/common";
import { useAppDispatch } from "@/redux/store";
import {
  updateFeedBackRating,
  updateFeedBackForm,
  setSelectedGeneration,
} from "@/redux/actions";
import Modal from "components/common/Modal";
import TextField from "components/common/TextField";
import SecondaryButton from "components/common/SecondaryButton";
import PrimaryButton from "@/components/common/PrimaryButton";

interface FeedbackProps {
  generationId: number;
  feedbackRating?: Nullable<string>;
  feedbackComment?: Nullable<string>;
  existingFeedbackTooSlow?: Nullable<boolean>;
  existingFeedbackWrongOutput?: Nullable<boolean>;
}

const Feedback: FC<FeedbackProps> = ({
  generationId,
  feedbackRating = null,
  feedbackComment = null,
  existingFeedbackTooSlow = null,
  existingFeedbackWrongOutput = null,
}) => {
  const dispatch = useAppDispatch();
  const [showFeedbackComment, setShowFeedbackComment] = useState(false);
  const [text, setText] = useState(feedbackComment || "");
  const [feedbackTooSlow, setFeedbackTooSlow] = useState(
    Boolean(existingFeedbackTooSlow)
  );
  const [feedbackWrongOutput, setFeedbackWringOutput] = useState(
    Boolean(existingFeedbackWrongOutput)
  );
  const { classes } = useStyles({
    feedbackRating,
    feedbackComment,
    feedbackTooSlow,
    feedbackWrongOutput,
  });

  const handleThumbsUp = () => {
    if (feedbackRating === "upvote") {
      dispatch(
        updateFeedBackRating({
          feedbackRating: null,
          generationId,
        })
      );
    } else {
      dispatch(
        updateFeedBackRating({
          feedbackRating: "upvote",
          generationId,
        })
      );
    }
    dispatch(setSelectedGeneration(generationId));
  };

  const handleThumbsDown = () => {
    if (feedbackRating === "downvote") {
      dispatch(
        updateFeedBackRating({
          feedbackRating: null,
          generationId,
        })
      );
    } else {
      dispatch(
        updateFeedBackRating({
          feedbackRating: "downvote",
          generationId,
        })
      );
    }
    dispatch(setSelectedGeneration(generationId));
  };

  const handleOpenFeedbackComment = () => {
    setShowFeedbackComment(true);
  };

  const handleCloseFeedbackComment = () => {
    setShowFeedbackComment(false);
  };

  const handleSubmitFeedbackComment = () => {
    setShowFeedbackComment(false);
    dispatch(
      updateFeedBackForm({
        feedbackComment: text,
        feedbackTooSlow,
        feedbackWrongOutput,
        generationId,
      })
    );
    dispatch(setSelectedGeneration(generationId));
  };
  const handleFeedbackTooSlow = () => {
    setFeedbackTooSlow(!feedbackTooSlow);
  };
  const handleFeedbackWrongOutput = () => {
    setFeedbackWringOutput(!feedbackWrongOutput);
  };

  return (
    <>
      <div className={classes.feedbackContainer}>
        <Typography className={classes.feedbackLabel}>
          How did we do?
        </Typography>
        <div>
          <IconButton
            className={classes.upvote}
            onClick={handleThumbsUp}
            aria-label="Feedback Upvote"
          >
            <MdThumbUp />
          </IconButton>
          <IconButton
            className={classes.downvote}
            onClick={handleThumbsDown}
            aria-label="Feedback Downvote"
          >
            <MdThumbDown />
          </IconButton>
          <IconButton
            className={classes.feedbackComment}
            onClick={handleOpenFeedbackComment}
            aria-label="Open Feedback Details Modal"
          >
            <MdComment />
          </IconButton>
        </div>
      </div>
      <Modal
        heading="Submit your feedback"
        open={showFeedbackComment}
        onClose={handleCloseFeedbackComment}
        className={classes.modal}
      >
        <div>
          <div className={classes.presetsContainer}>
            <SecondaryButton
              className={classes.feedbackTooSlow}
              onClick={handleFeedbackTooSlow}
            >
              Took too long
            </SecondaryButton>
            <SecondaryButton
              className={classes.feedbackWrongOutput}
              onClick={handleFeedbackWrongOutput}
            >
              Not what I wanted
            </SecondaryButton>
          </div>
          <TextField
            multiline={true}
            className={classes.textField}
            value={text}
            placeholder="Other..."
            onUpdate={setText}
          />
          <PrimaryButton
            color="secondary"
            className={classes.submitButton}
            onClick={handleSubmitFeedbackComment}
          >
            Submit
          </PrimaryButton>
        </div>
      </Modal>
    </>
  );
};

export default Feedback;
